import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box, Card, Table, Text, Badge, Button, Group, Modal,
  Select, Textarea, Stack, Alert, ActionIcon, Tooltip, Loader,
} from '@mantine/core'
import { IconCheck, IconX, IconEye, IconAlertCircle } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { registrationApi } from '@/api/registration'
import type { PendingRegistration, RegistrationDetail } from '@/api/registration'
import { permissionsApi } from '@/api/permissions'
import type { PermissionTemplate } from '@/api/permissions'
import { formatApiError } from '@/api/errors'

export type StatusNotificacao = { email: boolean; whatsapp: boolean } | undefined

/**
 * Decide o aviso a partir do status REAL dos canais que a API devolve.
 *
 * Campo ausente conta como falha, de proposito: backend antigo, deploy pela metade ou
 * erro de serializacao nao podem virar "notificado". Silencio nao e confirmacao — e foi
 * exatamente por afirmar sem olhar que a tela mentia (TASK-10485).
 */
export function avisoDeAprovacao(n: StatusNotificacao): { message: string; color: string } {
  const email = n?.email ?? false
  const zap = n?.whatsapp ?? false

  if (email && zap) {
    return {
      message: 'Cadastro aprovado. Usuário notificado por e-mail e WhatsApp.',
      color: 'green',
    }
  }
  if (email || zap) {
    const ok = email ? 'e-mail' : 'WhatsApp'
    const falhou = email ? 'WhatsApp' : 'e-mail'
    return {
      message: `Cadastro aprovado. Usuário notificado por ${ok}; o envio por ${falhou} falhou.`,
      color: 'yellow',
    }
  }
  return {
    message:
      'Cadastro aprovado, mas o usuário NÃO foi notificado — a senha temporária não chegou. Reenvie o convite.',
    color: 'red',
  }
}

/** Rotulo e cor do alerta de cadastro ja decidido.
 *
 * `expired` entrou na TASK-10484 e precisa de rotulo proprio: ate entao o alerta dizia
 * "Cadastro ja rejeitado" para tudo que nao fosse `approved`, e chamar de rejeitado o que
 * expirou e mentira. Cinza, e nao vermelho, porque expirar nao e uma decisao nova — e o
 * encerramento de uma; o §2.3 do design system reserva `red` para "rejeitado".
 */
const ROTULO_STATUS: Record<string, string> = {
  approved: 'Cadastro já aprovado',
  rejected: 'Cadastro já rejeitado',
  expired: 'Cadastro rejeitado e expirado',
}
const COR_STATUS: Record<string, string> = {
  approved: 'green',
  rejected: 'red',
  expired: 'gray',
}

/** Rotulo e cor de cada acao da trilha do cadastro. */
const ROTULO_ACAO: Record<string, string> = {
  submitted: 'enviado',
  approved: 'aprovado',
  rejected: 'rejeitado',
  expired: 'expirado',
  resubmitted: 'reenviado',
}
const COR_ACAO: Record<string, string> = {
  approved: 'green',
  rejected: 'red',
  expired: 'gray',
  // Laranja, e nao azul: reenvio pede uma decisao NOVA de quem ja decidiu uma vez.
  resubmitted: 'orange',
}

const ROTULO_CAMPO: Record<string, string> = {
  full_name: 'Nome', phone: 'Telefone', address: 'Endereço',
  organization_id: 'Organização', employee_number: 'Matrícula',
  cep: 'CEP', logradouro: 'Logradouro', numero: 'Número',
  complemento: 'Complemento', bairro: 'Bairro', cidade: 'Cidade', uf: 'UF',
}

/**
 * Extrai o de→para do `details` de um evento `resubmitted`.
 *
 * A organizacao vem como `{id, nome}` porque a trilha grava o que era verdade NA HORA
 * — resolver o nome na leitura mostraria o nome de hoje para um evento de ontem.
 */
function mudancas(details: Record<string, unknown>): [string, string, string][] {
  const changed = (details?.changed ?? {}) as Record<string, { de: unknown; para: unknown }>
  const texto = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—'
    if (typeof v === 'object' && 'nome' in (v as Record<string, unknown>)) {
      return String((v as { nome: unknown }).nome)
    }
    return String(v)
  }
  return Object.entries(changed).map(([campo, v]) => [campo, texto(v?.de), texto(v?.para)])
}

export function Registrations() {
  const [registrations, setRegistrations] = useState<PendingRegistration[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<RegistrationDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  // Approve modal
  const [approveOpen, setApproveOpen] = useState(false)
  const [approveId, setApproveId] = useState<number | null>(null)
  const [roleCode, setRoleCode] = useState<string | null>(null)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [templates, setTemplates] = useState<PermissionTemplate[]>([])
  const [approving, setApproving] = useState(false)

  // Reject modal
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectId, setRejectId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejecting, setRejecting] = useState(false)

  // Roles for dropdown
  const roleOptions = [
    { value: 'developer', label: 'Developer' },
    { value: 'devops', label: 'DevOps' },
    { value: 'viewer', label: 'Viewer' },
    { value: 'manager', label: 'Manager' },
    { value: 'gestor', label: 'Gestor' },
  ]

  const loadRegistrations = async () => {
    setLoading(true)
    try {
      const data = await registrationApi.listPending()
      setRegistrations(data)
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao carregar solicitações', color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadRegistrations() }, [])
  useEffect(() => { permissionsApi.listTemplates().then(setTemplates).catch(() => {}) }, [])

  // Deep link: /admin/registrations/:id abre o detalhe automaticamente.
  // Permite que a notificacao do sininho leve direto pra tela de analise.
  const { id: routeId } = useParams<{ id: string }>()
  useEffect(() => {
    if (!routeId) return
    const n = Number(routeId)
    if (!Number.isFinite(n) || n <= 0) return
    openDetail(n)
  }, [routeId])

  const openDetail = async (id: number) => {
    setSelectedId(id)
    setDetailLoading(true)
    try {
      const data = await registrationApi.getDetail(id)
      setDetail(data)
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao carregar detalhes', color: 'red' })
    } finally {
      setDetailLoading(false)
    }
  }

  /**
   * Conta o que REALMENTE aconteceu quando a decisao falha na volta.
   *
   * Em 09/09/2026 13:40:07 a recusa do cadastro 38 foi gravada e o e-mail saiu — e o
   * navegador do gestor largou a conexao aos ~636 ms (499 no nginx). Ele nao soube, e
   * 81 segundos depois tentou de novo. *Por que* o cliente desconectou nao foi medido:
   * 665 ms nao estoura timeout e o botao fica travado em `loading`. O que da para
   * consertar e' a tela nao deixa-lo no escuro.
   *
   * Devolve `true` quando a acao ja estava feita — nesse caso o erro generico nao deve
   * aparecer.
   */
  const contarEstadoReal = async (id: number, fallback: string): Promise<boolean> => {
    try {
      const atual = await registrationApi.getDetail(id)
      if (atual.status && atual.status !== 'pending_approval') {
        notifications.show({
          title: 'Já decidido',
          message:
            `${ROTULO_STATUS[atual.status] ?? 'Cadastro já processado'}`
            + `${atual.reviewed_by ? ` por ${atual.reviewed_by}` : ''}`
            + `${atual.reviewed_at ? ` em ${formatDate(atual.reviewed_at)}` : ''}.`,
          color: COR_STATUS[atual.status] ?? 'gray',
        })
        loadRegistrations()
        return true
      }
    } catch {
      // O GET tambem falhou: nao da para afirmar nada. Cai no erro generico.
    }
    notifications.show({ title: 'Erro', message: fallback, color: 'red' })
    return false
  }

  const handleApprove = async () => {
    if (!approveId || !roleCode || !templateId) return
    setApproving(true)
    try {
      const res = await registrationApi.approve(approveId, roleCode, Number(templateId))
      const aviso = avisoDeAprovacao(res?.notifications)
      notifications.show({ title: 'Aprovado', ...aviso, icon: <IconCheck size={16} /> })
      setApproveOpen(false)
      setRoleCode(null)
      setTemplateId(null)
      loadRegistrations()
    } catch (err: unknown) {
      setApproveOpen(false)
      await contarEstadoReal(approveId, formatApiError(err, 'Falha ao aprovar'))
    } finally {
      setApproving(false)
    }
  }

  const handleReject = async () => {
    if (!rejectId || rejectReason.trim().length < 3) return
    setRejecting(true)
    try {
      await registrationApi.reject(rejectId, rejectReason.trim())
      notifications.show({ title: 'Rejeitado', message: 'Cadastro rejeitado. Usuário notificado.', color: 'orange' })
      setRejectOpen(false)
      setRejectReason('')
      loadRegistrations()
    } catch (err: unknown) {
      setRejectOpen(false)
      await contarEstadoReal(rejectId, formatApiError(err, 'Falha ao rejeitar'))
    } finally {
      setRejecting(false)
    }
  }

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }
    catch { return d }
  }

  return (
    <Box>

      <Card radius="md" p="lg" style={{ backgroundColor: 'var(--ai-card-bg)', border: '1px solid var(--ai-border-light)' }}>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <Text fw={700} size="sm" c="var(--ai-text-primary)">Solicitações Pendentes</Text>
            {registrations.length > 0 && <Badge color="red" size="sm">{registrations.length}</Badge>}
          </Group>
          <Button variant="subtle" size="xs" onClick={loadRegistrations} loading={loading}>Atualizar</Button>
        </Group>

        {loading ? (
          <Box style={{ textAlign: 'center', padding: 32 }}><Loader size="sm" /></Box>
        ) : registrations.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">Nenhuma solicitação pendente.</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Organização</Table.Th>
                <Table.Th>Matrícula</Table.Th>
                <Table.Th>Data</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {registrations.map(reg => (
                <Table.Tr key={reg.id}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={500}>{reg.full_name}</Text>
                      {reg.resubmissions > 0 && (
                        <Badge color="orange" size="sm" variant="light">
                          Reenvio nº {reg.resubmissions}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm">{reg.email}</Text></Table.Td>
                  <Table.Td><Badge variant="light" size="sm">{reg.organization_name}</Badge></Table.Td>
                  <Table.Td><Text size="sm">{reg.employee_number}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{formatDate(reg.created_at)}</Text></Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="center">
                      <Tooltip label="Ver detalhes">
                        <ActionIcon aria-label="Ver detalhes" variant="subtle" color="blue" size="sm" onClick={() => openDetail(reg.id)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Aprovar">
                        <ActionIcon aria-label="Aprovar" variant="subtle" color="green" size="sm" onClick={() => { setApproveId(reg.id); setApproveOpen(true) }}>
                          <IconCheck size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Rejeitar">
                        <ActionIcon aria-label="Rejeitar" variant="subtle" color="red" size="sm" onClick={() => { setRejectId(reg.id); setRejectOpen(true) }}>
                          <IconX size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal opened={selectedId !== null} onClose={() => { setSelectedId(null); setDetail(null) }} title="Detalhes do Cadastro" size="lg">
        {detailLoading ? <Loader size="sm" /> : detail && (
          <Stack gap="sm">
            <Group><Text size="sm" fw={600} w={120}>Nome:</Text><Text size="sm">{detail.full_name}</Text></Group>
            <Group><Text size="sm" fw={600} w={120}>Email:</Text><Text size="sm">{detail.email}</Text></Group>
            <Group><Text size="sm" fw={600} w={120}>Telefone:</Text><Text size="sm">{detail.phone}</Text></Group>
            <Group><Text size="sm" fw={600} w={120}>Endereço:</Text><Text size="sm">{detail.address}</Text></Group>
            <Group><Text size="sm" fw={600} w={120}>Organização:</Text><Badge variant="light">{detail.organization_name}</Badge></Group>
            <Group><Text size="sm" fw={600} w={120}>Matrícula:</Text><Text size="sm">{detail.employee_number}</Text></Group>
            <Group><Text size="sm" fw={600} w={120}>Status:</Text><Badge color={detail.status === 'approved' ? 'green' : detail.status === 'rejected' ? 'red' : 'yellow'}>{detail.status}</Badge></Group>
            <Group><Text size="sm" fw={600} w={120}>Data:</Text><Text size="sm">{formatDate(detail.created_at)}</Text></Group>

            {detail.audit_log.length > 0 && (
              <>
                <Text size="sm" fw={700} mt="md">Histórico</Text>
                {detail.audit_log.map((log, i) => (
                  <Box key={i}>
                    <Group gap="xs">
                      <Badge size="xs" variant="dot" color={COR_ACAO[log.action] ?? 'blue'}>
                        {ROTULO_ACAO[log.action] ?? log.action}
                      </Badge>
                      <Text size="xs" c="dimmed">{log.performed_by} - {formatDate(log.created_at)}</Text>
                    </Group>
                    {/* O de→para so aparece no reenvio, e e a informacao que faz a
                        segunda decisao custar segundos em vez de uma investigacao. */}
                    {log.action === 'resubmitted' && mudancas(log.details).map(([campo, de, para]) => (
                      <Text key={campo} size="xs" c="dimmed" ml={22}>
                        {ROTULO_CAMPO[campo] ?? campo}: {de} → {para}
                      </Text>
                    ))}
                  </Box>
                ))}
              </>
            )}

            {/* Acao so existe em cadastro pendente. O deep link do sino chega
                aqui com cadastro ja decidido (TASK-10443): ate 08/09/2026 esta
                tela mostrava o selo verde `approved` E os botoes, e o unico
                freio era o 404 do backend, depois do formulario preenchido. */}
            {detail.status === 'pending_approval' ? (
              <Group justify="center" mt="md">
                <Button color="green" leftSection={<IconCheck size={16} />} onClick={() => { setSelectedId(null); setApproveId(detail.id); setApproveOpen(true) }}>
                  Aprovar
                </Button>
                <Button color="red" variant="outline" leftSection={<IconX size={16} />} onClick={() => { setSelectedId(null); setRejectId(detail.id); setRejectOpen(true) }}>
                  Rejeitar
                </Button>
              </Group>
            ) : (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color={COR_STATUS[detail.status] ?? 'gray'}
                radius="sm"
                mt="md"
              >
                <Text size="sm">
                  {ROTULO_STATUS[detail.status] ?? 'Cadastro já processado'}
                  {detail.reviewed_by ? ` por ${detail.reviewed_by}` : ''}
                  {detail.reviewed_at ? ` em ${formatDate(detail.reviewed_at)}` : ''}. Não há ação pendente.
                </Text>
                {['rejected', 'expired'].includes(detail.status) && detail.rejection_reason && (
                  <Text size="xs" mt={4}>Motivo: {detail.rejection_reason}</Text>
                )}
              </Alert>
            )}
          </Stack>
        )}
      </Modal>

      {/* Approve Modal */}
      <Modal opened={approveOpen} onClose={() => setApproveOpen(false)} title="Aprovar Cadastro" size="sm">
        <Stack gap="md">
          <Text size="sm">Selecione o perfil (role) para este usuário:</Text>
          <Select
            label="Perfil"
            placeholder="Selecione..."
            data={roleOptions}
            value={roleCode}
            onChange={setRoleCode}
            required
          />
          <Select
            label="Perfil de permissões"
            placeholder="Selecione o template..."
            data={templates.map((t) => ({ value: String(t.id), label: t.name }))}
            value={templateId}
            onChange={setTemplateId}
            searchable
            nothingFoundMessage="Nenhum template encontrado"
            required
          />
          <Alert color="blue" radius="sm">
            <Text size="xs">Ao aprovar, uma conta sera criada automaticamente e o usuário recebera a senha temporária por email e WhatsApp.</Text>
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setApproveOpen(false)}>Cancelar</Button>
            <Button color="green" loading={approving} disabled={!roleCode || !templateId} onClick={handleApprove}>
              Confirmar Aprovação
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Modal */}
      <Modal opened={rejectOpen} onClose={() => setRejectOpen(false)} title="Rejeitar Cadastro" size="sm">
        <Stack gap="md">
          <Textarea
            label="Motivo da Rejeição"
            placeholder="Informe o motivo da rejeição (mínimo 10 caracteres)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            minRows={3}
            required
          />
          {rejectReason.length > 0 && rejectReason.trim().length < 10 && (
            <Alert icon={<IconAlertCircle size={16} />} color="orange" radius="sm">
              <Text size="xs">O motivo deve ter pelo menos 10 caracteres.</Text>
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button color="red" loading={rejecting} disabled={rejectReason.trim().length < 10} onClick={handleReject}>
              Confirmar Rejeição
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
